import sdk from '../../../services/service-awards-sdk';
import { getString } from '@nackle/intl-tools';
import { env } from 'endpoints';

/*******************************************************************************
 * Initial state
 */
const initialState = {
    recipients: [],
    recipientsLoading: false,
    recipientsLoadingError: null,
    programsLoading: false,
    programIssues: {},
    procedures: [],
    proceduresLoading: false,
    proceduresLoaded: false,
    proceduresClearing: false
};

/*******************************************************************************
 * Non-async action types
 */
export const types = {
    setPrograms: 'program-list/setPrograms',
    setProgramsLoading: 'program-list/setPrograms/loading',
    setProgramsLoaded: 'program-list/setPrograms/loaded',

    setProgramIssues: 'program-list/setProgramIssues',
    setProgramIssuesLoading: 'program-list/setProgramIssues/loading',
    setProgramIssuesLoaded: 'program-list/setProgramIssues/loaded',
    setProgramIssuesFinding: 'program-list/setProgramIssues/finding',

    setProcedures: 'program-list/setProcedures',
    setProceduresLoading: 'program-list/setProcedures/loading',
    setProceduresLoaded: 'program-list/setProcedures/loaded',
    setProceduresClearing: 'program-list/setProcedures/clearing'
};

/*******************************************************************************
 * Reducer
 */
export default function programListReducer( state = initialState, action ) {
    switch ( action.type ) {
        case types.setPrograms:
            action.programs.map( program => {
                return program.programName = getString( `program_${ program.programId }.name` );
            } );

            return {
                ...state,
                programs: action.programs
            };

        case types.setProgramsLoading:
            return {
                ...state,
                programsLoading: action.loading
            };

        case types.setProgramsLoaded:
            return {
                ...state,
                programsLoaded: action.loaded
            };

        case types.setProgramIssues:
            return {
              ...state,
              programIssues: {
                  ...state.programIssues,
                  [ action.programId ]: {
                      ...( state.programIssues [ action.programId ] || {} ),
                      issues: action.issues
                    }
                }
            };

        case types.setProgramIssuesLoading:
            return {
                ...state,
                programIssues: {
                    ...state.programIssues,
                    [ action.programId ]: {
                        ...( state.programIssues [ action.programId ] || {} ),
                        loading: action.loading
                    }
                }
            };

        case types.setProgramIssuesLoaded:
            return {
                ...state,
                programIssues: {
                    ...state.programIssues,
                    [ action.programId ]: {
                        ...( state.programIssues [ action.programId ] || {} ),
                        loading: action.loading
                    }
                }
            };

            case types.setProgramIssuesFinding:
                return {
                    ...state,
                    programIssues: {
                        ...state.programIssues,
                        [ action.programId ]: {
                            ...( state.programIssues [ action.programId ] || {} ),
                            finding: action.finding
                        }
                    }
                };

            case types.setProcedures:
                return {
                    ...state,
                    procedures: action.procedures
                };

            case types.setProceduresLoading:
                return {
                    ...state,
                    proceduresLoading: action.loading
                };

            case types.setProceduresLoaded:
                return {
                    ...state,
                    proceduresLoaded: action.loaded
                };

            case types.setProceduresClearing:
                return {
                    ...state,
                    proceduresClearing: action.clearing
                };

        default:
            return state;
    }
}

/*******************************************************************************
 * Action creators
 */
export function loadPrograms( locale ) {
    return ( dispatch ) => {
        dispatch( setProgramsLoading( true ) );

        return sdk.getPrograms()
            .then( ( programs ) => {
                dispatch( setProgramsLoading( false ) );
                dispatch( setPrograms( programs ) );
                dispatch( setProgramsLoaded( true ) );

                programs.forEach( ( program ) => {
                    dispatch( loadProgramIssues( program.programId ) );
                } );

                return programs;
            } )
            .catch( ( err ) => {
                console.warn( err );
                dispatch( setPrograms( [] ) );
                dispatch( setProgramsLoading( false ) );
                dispatch( setProgramsLoaded( true ) );

                return [];
            } );
    };
}

export function loadProgramIssues( programId ) {
    return ( dispatch ) => {
        dispatch( setProgramIssuesLoading( programId, true ) );

        return sdk.getProgramIssues( programId )
            .then( ( issues ) => {
                dispatch( setProgramIssuesLoading( programId, false ) );
                dispatch( setProgramIssues( programId, createIssuesReport( issues ) ) );
                dispatch( setProgramIssuesLoaded( programId, true ) );

                return issues;
            } )
            .catch( (  ) => {
                dispatch( setProgramIssuesLoading( programId, false ) );
                dispatch( setProgramIssues( programId, null ) );
                dispatch( setProgramIssuesLoaded( programId, true ) );

                return null;
            } );
    };
}

export function loadProcedures() {
    return ( dispatch ) => {
        dispatch( setProceduresLoading( true ) );

        return sdk.getProcedureRetry()
            .then( ( { procedures } ) => {
                dispatch( setProceduresLoading( false ) );
                dispatch( setProcedures( procedures ) );
                dispatch( setProceduresLoaded( true ) );

                return procedures;
            } )
            .catch( ( ) => {
                dispatch( setProceduresLoading( false ) );
                dispatch( setProcedures( [] ) );
                dispatch( setProceduresLoaded( true ) );

                return [];
            } );
    };
}

export function clearProcedureAttempts( procedureKey, programId ) {
    return ( dispatch ) => {
        dispatch( setProceduresClearing( true ) );

        return sdk.clearProcedureAttemptsForProgram( procedureKey, programId )
            .then( ( { procedures } ) => {
                dispatch( setProceduresClearing( false ) );
            } )
            .catch( (  ) => {
                dispatch( setProceduresClearing( false ) );

                return [];
            } );
    };
}


function setPrograms( programs ) {
    return {
        type: types.setPrograms,
        programs
    };
}

function setProgramsLoading( loading ) {
    return {
        type: types.setProgramsLoading,
        loading
    };
}

function setProgramsLoaded( loaded ) {
    return {
        type: types.setProgramsLoaded,
        loaded
    };
}

function setProgramIssues( programId, issues ) {
    return {
        type: types.setProgramIssues,
        programId,
        issues
    };
}

function setProgramIssuesLoading( programId, loading ) {
    return {
        type: types.setProgramIssuesLoading,
        programId,
        loading
    };
}

function setProgramIssuesLoaded( programId, loaded ) {
    return {
        type: types.setProgramIssuesLoaded,
        programId,
        loaded
    };
}


export function findProgramIssues( programId ) {
    return ( dispatch ) => {
        dispatch( setProgramIssuesFinding( programId, true ) );
        try {
            return sdk.findProgramIssues( programId )
                .then( () => {
                    // poll issues endpoint until it's done.
                    const interval = setInterval( async () => {
                        const issues = await sdk.getProgramIssues( programId );
                        if ( issues  ) {
                           dispatch( loadProgramIssues( programId ) );
                           dispatch( setProgramIssuesFinding( programId, false ) );
                           clearInterval( interval );
                        }
                    }, 10000 );
                } )
                .catch( ( err ) => {
                    dispatch( setProgramIssuesFinding( programId, false ) );
                    return null;
                } );
        } catch {
            dispatch( setProgramIssuesFinding( programId, false ) );
            return null;
        }
    };
}

function setProgramIssuesFinding( programId, finding ) {
    return {
        type: types.setProgramIssuesFinding,
        programId,
        finding
    };
}


function createIssuesReport( issues ) {
    if ( !issues ) {
        return null;
    }

    const report = [];

    const cmpUrl = `https://${ env ? env + '.' : '' }company-studio.biw.cloud`;
    const saUrl = `https://${ env ? env + '.' : '' }service-awards-studio.biw.cloud`;

    // company issues
    issues.company.errors.forEach( ( error ) => {
        report.push( {
            message: `Company: ${ error.message }`,
            link: `${ cmpUrl }/companies/${ issues.company.companyId }`
        } );
    } );

    // program validation issues
    issues.program.errors.forEach( ( error ) => {
        report.push( {
            message: `Program: ${ error.message }`,
            link: `${ saUrl }/${ issues.company.companyId }/programs/${ issues.program.programId }/${ error.programSetupStep || 'basics' }`
         } );
    } );

    // program reward issues
    issues.rewards.forEach( ( reward )=> {
        reward.errors.forEach( ( error ) => {
            report.push( {
                message: `Program: ${ error.message }`,
                link: `${ saUrl }/${ issues.company.companyId }/programs/${ issues.program.programId }/awards`
             } );
        } );
    } );

    // program milestone issues
    issues.milestones.forEach( ( milestone )=> {
        milestone.errors.forEach( ( error ) => {
            report.push( {
                message: `Program: ${ error.message }`,
                link: `${ saUrl }/${ issues.company.companyId }/programs/${ issues.program.programId }/milestones`
             } );
        } );
    } );

    // program celebration issues
    issues.celebration.errors.forEach( ( error ) => {
        report.push( {
            message: `Program: ${ error.message }`,
            link: `${ saUrl }/${ issues.company.companyId }/programs/${ issues.program.programId }/celebration`
        } );
    } );

    // recipient errors
    issues.recipients.forEach( ( recipient ) => {
        recipient.errors.forEach( ( error ) => {
            report.push( {
                message: `Recipient: ${ error.message }`,
                link: `${ saUrl }/${ issues.company.companyId }/programs/${ issues.program.programId }/recipient/${ recipient.recipientId }`
             } );
        } );
    } );

    return {
        count: report.length,
        report
    };
}


function setProcedures( procedures ) {
    return {
        type: types.setProcedures,
        procedures
    };
}

function setProceduresLoading( loading ) {
    return {
        type: types.setProceduresLoading,
        loading
    };
}

function setProceduresLoaded( loaded ) {
    return {
        type: types.setProceduresLoaded,
        loaded
    };
}

function setProceduresClearing( clearing ) {
    return {
        type: types.setProceduresClearing,
        clearing
    };
}

export const actions = {
    loadPrograms,
    findProgramIssues,
    loadProcedures,
    clearProcedureAttempts
};
